/* eslint-disable @typescript-eslint/no-restricted-imports */
import { useMemo } from 'react';
import { matchPath, useLocation, useParams } from 'react-router-dom-v5-compat';

export {
  BrowserRouter,
  Prompt,
  MemoryRouter,
  Route,
  Switch,
  useHistory,
  // Temporary for IDs whose consumers still require v5/history encoding.
  // Migrate callers with their v5 route hosts and URL/API encoding contracts.
  useRouteMatch as useLegacyRouteMatch,
} from 'react-router-dom';

export {
  CompatRouter,
  CompatRoute,
  Routes,
  Route as RouteV6,
  Navigate,
  useNavigate,
  useLocation,
  useParams,
  useMatch,
  matchPath,
  generatePath,
  Link,
  NavLink,
  type LinkProps,
} from 'react-router-dom-v5-compat';

const hasRequiredRouteParams = <Key extends string>(
  params: Readonly<Record<string, string | undefined>>,
  requiredKeys: readonly Key[]
): params is Readonly<Record<Key, string>> =>
  requiredKeys.every(
    (key) => typeof params[key] === 'string' && params[key] !== ''
  );

/** Require matched parameters before calling page data hooks. */
export const useRequiredRouteParams = <Key extends string>(
  ...requiredKeys: [Key, ...Key[]]
): Readonly<Record<Key, string>> => {
  const params = useParams();
  if (!hasRequiredRouteParams(params, requiredKeys)) {
    // A page mounted under an incompatible route is a programming error.
    throw new Error(
      `Missing required route parameters: ${requiredKeys.join(', ')}`
    );
  }
  return params;
};

/** Caller-typed route state; direct entry may have no state. */
export const useLocationState = <State = unknown>() =>
  useLocation().state as State | null | undefined;

/** matchPath decodes %2F; restore it before treating a matched segment as a raw URL/key. */
export const getEncodedPathParam = (value: string | undefined) =>
  value?.replace(/\//g, '%2F');

/** Keep the original suffix bytes, including encoded slashes and trailing slashes. */
export const getAppPathname = (pathname: string): string | undefined => {
  const match = matchPath(
    { path: '/company/:companyKey/app/:appKey/*', end: false },
    pathname
  );
  if (!match) return undefined;
  const suffix = pathname.slice(match.pathnameBase.length);
  // The old splat requires the slash after appKey. Preserve that root distinction.
  return suffix.startsWith('/') ? suffix : undefined;
};

// This hook is useful only within /company/:id/app/:id/ URL space.
export const useAppLocation = () => {
  const location = useLocation();
  const pathname = getAppPathname(location.pathname);
  const suffix =
    pathname === undefined
      ? undefined
      : pathname + location.search + location.hash;
  const appUrl = suffix === '/' ? undefined : suffix;

  return useMemo(() => ({ appUrl }), [appUrl]);
};
